import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AlertTriangle, CalendarDays, CheckCircle2, ChevronDown, ChevronRight, CircleHelp,
  Clock3, CloudRain, Database, ExternalLink, Factory, Flame, Languages, MapPin, Menu,
  Radio, RefreshCw, Search, ShieldCheck, SlidersHorizontal, TrainFront, Wifi, X
} from 'lucide-react';
import MapView from './map/MapView.jsx';
import './styles.css';

const copy = {
  nl: {
    subtitle: 'Situationeel beeld voor Nederland', search: 'Zoek plaats of gebeurtenis',
    events: 'gebeurtenissen', latest: 'Nieuwste eerst', filters: 'Filters', all: 'Alle',
    categories: 'Categorieën', status: 'Status', severity: 'Ernst', updated: 'Laatste update',
    sources: 'bronnen', underway: 'Bezig', scheduled: 'Gepland', confirmed: 'Bevestigd',
    preliminary: 'Voorlopig', concluded: 'Afgesloten', critical: 'Kritiek', high: 'Hoog', medium: 'Gemiddeld', low: 'Laag',
    civil_unrest: 'Burgeronrust', infrastructure: 'Infrastructuur', transport: 'Transport',
    emergency: 'Noodgeval', weather: 'Weer', cyber: 'Cyber', economy: 'Economie',
    overview: 'Overzicht', impact: 'Praktische impact', evidence: 'Bronnen', timeline: 'Tijdlijn',
    location: 'Locatie', started: 'Begonnen', published: 'Gepubliceerd', confidence: 'Vertrouwen',
    noEvents: 'Geen gebeurtenissen binnen deze filters.', loading: 'Actuele informatie laden…',
    sourceNote: 'Bronbeoordeling', viewSource: 'Open originele bron', lastVerified: 'Laatst geverifieerd',
    mapLabel: 'Kaart met actuele gebeurtenissen in Nederland', reset: 'Wis filters',
    menu: 'Menu', refreshData: 'Gegevens verversen', sourceStatus: 'Bronstatus', activeSources: 'actieve bronnen',
    openSourceApi: 'Open volledig bronnenoverzicht', eventsHere: 'Gebeurtenissen op deze locatie', close: 'Sluiten',
    expandEvents: 'Open gebeurtenissenlijst', collapseEvents: 'Klap gebeurtenissenlijst in'
  },
  en: {
    subtitle: 'Situational awareness for the Netherlands', search: 'Search place or event',
    events: 'events', latest: 'Newest first', filters: 'Filters', all: 'All',
    categories: 'Categories', status: 'Status', severity: 'Severity', updated: 'Last update',
    sources: 'sources', underway: 'Underway', scheduled: 'Scheduled', confirmed: 'Confirmed',
    preliminary: 'Preliminary', concluded: 'Concluded', critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low',
    civil_unrest: 'Civil unrest', infrastructure: 'Infrastructure', transport: 'Transport',
    emergency: 'Emergency', weather: 'Weather', cyber: 'Cyber', economy: 'Economy',
    overview: 'Overview', impact: 'Practical impact', evidence: 'Sources', timeline: 'Timeline',
    location: 'Location', started: 'Started', published: 'Published', confidence: 'Confidence',
    noEvents: 'No events match these filters.', loading: 'Loading current information…',
    sourceNote: 'Source assessment', viewSource: 'Open original source', lastVerified: 'Last verified',
    mapLabel: 'Map of current events in the Netherlands', reset: 'Clear filters',
    menu: 'Menu', refreshData: 'Refresh data', sourceStatus: 'Source status', activeSources: 'active sources',
    openSourceApi: 'Open full source overview', eventsHere: 'Events at this location', close: 'Close',
    expandEvents: 'Open event list', collapseEvents: 'Collapse event list'
  }
};

const categoryMeta = {
  civil_unrest: { icon: Radio, color: '#df6a21' }, emergency: { icon: Flame, color: '#c84343' },
  infrastructure: { icon: Factory, color: '#167f82' }, transport: { icon: TrainFront, color: '#176eb3' },
  weather: { icon: CloudRain, color: '#247f80' }, cyber: { icon: Wifi, color: '#6842b3' },
  economy: { icon: Database, color: '#c89500' }
};

const fmt = (iso, lang) => new Intl.DateTimeFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam'
}).format(new Date(iso));

const timeOnly = (iso, lang) => new Intl.DateTimeFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam'
}).format(new Date(iso));

function sourceLabel(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return 'Bron'; }
}

function StatusMark({ status, t }) {
  const Icon = status === 'confirmed' ? ShieldCheck : status === 'scheduled' ? CalendarDays : status === 'concluded' ? CheckCircle2 : status === 'preliminary' ? CircleHelp : Clock3;
  return <span className={`status status--${status}`}><Icon size={14} />{t[status] || status}</span>;
}

function FilterMenu({ t, categories, toggleCategory, clear, counts, open, setOpen, menuRef }) {
  return <div className="filter-control" ref={menuRef}>
    <button className="filter-trigger" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-haspopup="dialog">
      <SlidersHorizontal size={18}/><span>{t.filters}</span>{categories.size ? <b>{categories.size}</b> : null}<ChevronDown size={16}/>
    </button>
    {open ? <div className="filter-popover" role="dialog" aria-label={t.filters}>
      <header><strong>{t.categories}</strong><button onClick={clear}>{t.reset}</button></header>
      <div className="filter-grid">
        <button className={categories.size ? 'category-filter' : 'category-filter active'} onClick={clear} aria-pressed={!categories.size}><span className="filter-symbol filter-symbol--all">{t.all.slice(0,1)}</span><span>{t.all}</span><b>{Object.values(counts).reduce((sum, count) => sum + count, 0)}</b></button>
        {Object.entries(categoryMeta).map(([key, meta]) => {
          const Icon = meta.icon; const active = categories.has(key);
          return <button key={key} className={active ? 'category-filter active' : 'category-filter'} onClick={() => toggleCategory(key)} aria-pressed={active}>
            <span className="filter-symbol" style={{'--category-color': meta.color}}><Icon size={16}/></span><span>{t[key]}</span><b>{counts[key] || 0}</b>
          </button>;
        })}
      </div>
    </div> : null}
  </div>;
}

const EventList = memo(function EventList({ events, selected, select, t, lang, loading, collapsed, setCollapsed }) {
  const dragStart = useRef(null); const dragged = useRef(false);
  const finishDrag = event => {
    if (dragStart.current === null) return;
    const distance = event.clientY - dragStart.current; dragStart.current = null;
    if (Math.abs(distance) < 30) return;
    dragged.current = true; setCollapsed(distance > 0);
  };
  return <section className="event-rail" aria-label={`${events.length} ${t.events}`}>
    <button
      className="event-sheet-toggle"
      type="button"
      aria-expanded={!collapsed}
      aria-label={collapsed ? t.expandEvents : t.collapseEvents}
      onClick={() => { if (dragged.current) { dragged.current = false; return; } setCollapsed(!collapsed); }}
      onPointerDown={event => { dragStart.current = event.clientY; dragged.current = false; event.currentTarget.setPointerCapture(event.pointerId); }}
      onPointerUp={finishDrag}
      onPointerCancel={() => { dragStart.current = null; dragged.current = false; }}
    ><span aria-hidden="true"/></button>
    <header className="event-rail-header"><div><strong>{events.length}</strong> {t.events}</div><span>{t.latest}<ChevronDown size={14}/></span></header>
    <div className="event-scroll">
      {loading ? <div className="empty"><RefreshCw className="spin"/> {t.loading}</div> : null}
      {!loading && !events.length ? <div className="empty">{t.noEvents}</div> : null}
      {events.map(event => {
        const meta = categoryMeta[event.category] || { icon: AlertTriangle, color: '#667786' }; const Icon = meta.icon;
        return <button className={`event-row ${selected?.id === event.id ? 'selected' : ''}`} key={event.id} onClick={() => select(event)}>
          <span className="event-icon" style={{'--event-color': meta.color}}><Icon size={21}/></span>
          <span className="event-copy">
            <span className="event-meta"><span style={{'--event-color': meta.color}}><i/>{t[event.category] || event.category}</span><time>{timeOnly(event.published_at, lang)}</time></span>
            <strong>{event.title}</strong>
            <small>{event.location_name}</small>
            <StatusMark status={event.status} t={t}/>
          </span>
          <ChevronRight className="event-chevron" size={19}/>
        </button>;
      })}
    </div>
  </section>;
});

function Detail({ event, t, lang, close }) {
  if (!event) return null;
  const meta = categoryMeta[event.category] || { icon: AlertTriangle, color: '#667786' };
  return <aside className="detail" aria-label={event.title} style={{'--event-color': meta.color}}>
    <button className="detail-close" onClick={close} aria-label={t.close}><X size={24}/></button>
    <div className="detail-category"><i/>{t[event.category] || event.category}</div>
    <h2>{event.title}</h2>
    <p className="detail-location"><MapPin size={16}/>{event.location_name}</p>
    <time className="detail-published">{fmt(event.occurred_at || event.published_at, lang)}</time>
    <div className="detail-flags"><StatusMark status={event.status} t={t}/><span className={`severity severity--${event.severity}`}><AlertTriangle size={14}/>{t[event.severity] || event.severity}</span></div>
    <section className="detail-summary"><h3>{t.overview}</h3><p>{event.summary}</p></section>
    <dl className="detail-facts">
      <div><dt><ExternalLink size={16}/>{t.evidence}</dt><dd>{sourceLabel(event.source_url)}</dd></div>
      <div><dt><Clock3 size={16}/>{t.lastVerified}</dt><dd>{fmt(event.last_verified_at, lang)}</dd></div>
      <div><dt><ShieldCheck size={16}/>{t.confidence}</dt><dd>{event.confidence}%</dd></div>
      <div><dt><MapPin size={16}/>{t.location}</dt><dd>{event.coordinate_precision}</dd></div>
    </dl>
    <section className="detail-impact"><h3>{t.impact}</h3><p>{event.impact || (lang === 'nl' ? 'Geen bredere impact bevestigd.' : 'No wider impact confirmed.')}</p></section>
    <a className="source-button" href={event.source_url} target="_blank" rel="noreferrer"><ExternalLink size={17}/>{t.viewSource}<ChevronRight size={17}/></a>
  </aside>;
}

function App() {
  const [lang, setLang] = useState('nl'); const t = copy[lang];
  const [events, setEvents] = useState([]); const [selected, setSelected] = useState(null);
  const [categories, setCategories] = useState(() => new Set()); const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(''); const [mapFocusRequest, setMapFocusRequest] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false); const [filtersOpen, setFiltersOpen] = useState(false); const [sourceStats, setSourceStats] = useState(null);
  const [mapGroup, setMapGroup] = useState(null); const menuRef = useRef(null); const filterRef = useRef(null);
  const [mobileRailCollapsed, setMobileRailCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/events'); const data = await res.json(); const nextEvents = data.events || [];
      setEvents(nextEvents);
      setSelected(current => current || (window.matchMedia('(min-width: 761px)').matches ? nextEvents[0] || null : null));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const timer = setInterval(load, 60000); return () => clearInterval(timer); }, [load]);
  useEffect(() => {
    if (!menuOpen && !filtersOpen) return undefined;
    const close = event => {
      if (event.key === 'Escape') { setMenuOpen(false); setFiltersOpen(false); return; }
      if (event.type === 'pointerdown') {
        if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
        if (!filterRef.current?.contains(event.target)) setFiltersOpen(false);
      }
    };
    document.addEventListener('keydown', close); document.addEventListener('pointerdown', close);
    return () => { document.removeEventListener('keydown', close); document.removeEventListener('pointerdown', close); };
  }, [menuOpen, filtersOpen]);

  const toggleMenu = useCallback(async () => {
    const opening = !menuOpen; setMenuOpen(opening); setFiltersOpen(false); if (!opening) return;
    try {
      const response = await fetch('/api/sources'); const data = await response.json(); const sources = data.sources || [];
      setSourceStats({ total: sources.length, active: sources.filter(source => source.status === 'ok').length, errors: sources.filter(source => source.status === 'error').length });
    } catch { setSourceStats(null); }
  }, [menuOpen]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase(lang === 'nl' ? 'nl-NL' : 'en-GB');
    return events.filter(event => (!categories.size || categories.has(event.category)) && (!normalized || `${event.title} ${event.summary} ${event.location_name}`.toLocaleLowerCase().includes(normalized)));
  }, [events, categories, query, lang]);
  const counts = useMemo(() => events.reduce((result, event) => ({ ...result, [event.category]: (result[event.category] || 0) + 1 }), {}), [events]);
  const latestUpdate = useMemo(() => events.reduce((latest, event) => Math.max(latest, new Date(event.last_verified_at).getTime() || 0), 0), [events]);
  const toggleCategory = useCallback(key => setCategories(previous => { const next = new Set(previous); next.has(key) ? next.delete(key) : next.add(key); return next; }), []);
  const clearFilters = useCallback(() => { setCategories(new Set()); setQuery(''); }, []);
  const selectEvent = useCallback(event => { setSelected(event); setMapGroup(null); setMapFocusRequest({ longitude: +event.longitude, latitude: +event.latitude, request: Date.now() }); }, []);
  const selectMapGroup = useCallback(group => { const event = group[0]; setMapGroup(group); setMapFocusRequest({ longitude: +event.longitude, latitude: +event.latitude, request: Date.now() }); }, []);

  return <div className="app">
    <header className="topbar">
      <div className="brand"><span className="brand-mark"><ShieldCheck size={25}/></span><span className="brand-name">SITREP NL</span><small>{t.subtitle}</small></div>
      <label className="topbar-search"><Search size={20}/><input aria-label={t.search} placeholder={t.search} value={query} onChange={event => setQuery(event.target.value)}/>{query ? <button onClick={() => setQuery('')} aria-label={t.reset}><X size={17}/></button> : null}</label>
      <FilterMenu t={t} categories={categories} toggleCategory={toggleCategory} clear={clearFilters} counts={counts} open={filtersOpen} setOpen={value => { setFiltersOpen(value); setMenuOpen(false); }} menuRef={filterRef}/>
      <div className="top-actions" ref={menuRef}>
        <span className="live"><i/> LIVE</span>
        <button className="language-button" onClick={() => setLang(lang === 'nl' ? 'en' : 'nl')}><Languages size={17}/>{lang.toUpperCase()}</button>
        <button className="menu-button" onClick={toggleMenu} aria-label={t.menu} aria-expanded={menuOpen} aria-haspopup="menu"><Menu size={23}/></button>
        {menuOpen ? <div className="app-menu" role="menu"><strong>{t.menu}</strong><button role="menuitem" onClick={async () => { await load(); setMenuOpen(false); }}><RefreshCw size={16}/><span>{t.refreshData}</span></button><div className="menu-status"><ShieldCheck size={16}/><span><b>{t.sourceStatus}</b><small>{sourceStats ? `${sourceStats.active}/${sourceStats.total} ${t.activeSources}${sourceStats.errors ? ` · ${sourceStats.errors} errors` : ''}` : '…'}</small></span></div><a role="menuitem" href="/api/sources" target="_blank" rel="noreferrer"><Database size={16}/><span>{t.openSourceApi}</span></a></div> : null}
      </div>
    </header>
    <main className={`workspace${selected ? ' has-selection' : ''}${mobileRailCollapsed ? ' rail-collapsed' : ''}`}>
      <EventList events={filtered} selected={selected} select={selectEvent} t={t} lang={lang} loading={loading} collapsed={mobileRailCollapsed} setCollapsed={setMobileRailCollapsed}/>
      <div className="map-wrap">
        <MapView events={filtered} selected={selected} onSelect={selectEvent} onSelectGroup={selectMapGroup} focusRequest={mapFocusRequest} label={t.mapLabel}/>
        {mapGroup ? <div className="map-group-panel"><header><strong>{t.eventsHere}</strong><button onClick={() => setMapGroup(null)} aria-label={t.close}><X size={20}/></button></header>{mapGroup.map(event => <button key={event.id} onClick={() => selectEvent(event)}><span><b>{event.title}</b><small>{fmt(event.occurred_at || event.published_at, lang)} · {event.location_name}</small></span><ChevronRight size={16}/></button>)}</div> : null}
        <div className="map-update"><i/>{t.updated}: {latestUpdate ? fmt(latestUpdate, lang) : '—'}<RefreshCw size={13}/></div>
        <Detail event={selected} t={t} lang={lang} close={() => setSelected(null)}/>
      </div>
    </main>
  </div>;
}

createRoot(document.getElementById('root')).render(<React.StrictMode><App/></React.StrictMode>);
